/**
 * Cache Control — per-domain CDN cache management for the Multi-CDN tab. Pick a
 * domain, then manage its cache rules (list + add), toggle tiered (multi-layer)
 * caching, and submit / track cache purges.
 *
 * Backed by `/waf/cache/*` (Owl/Mlytics, customer-scoped). The backend exposes
 * List + Create for rules only (no edit/delete) and no getter for the current
 * tiered-cache state, so those are surfaced as one-way controls.
 */
import { useEffect, useMemo, useState } from 'react';
import { Gauge, Plus, RotateCcw, Layers, Trash2, Pencil } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import {
  Badge,
  Skeleton,
  Button,
  IconButton,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafCacheRules,
  useWafCacheOptions,
  useSetTieredCache,
  useWafCachePurges,
  useDeleteWafCacheRule,
  fmtEpoch,
  wafExtractError,
  type WafCacheOption,
  type WafCacheRule,
  type WafCachePurge,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import { CacheRuleFormDialog } from './CacheRuleFormDialog';
import { CachePurgeDialog } from './CachePurgeDialog';
import styles from './WafCacheControl.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;
const mono = { fontFamily: 'var(--vn-font-mono)' } as const;

/** Rule/purge paths, truncated to a couple chips + a `+N` overflow. */
function pathsCell(paths: string[] | undefined, max = 3) {
  const items = paths ?? [];
  if (items.length === 0) return muted('—');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...mono }}>
      {items.slice(0, max).join(', ')}
      {items.length > max ? muted(` +${items.length - max}`) : null}
    </span>
  );
}

/** Status → badge tone for a purge job. */
function purgeTone(status: string): 'success' | 'warning' | 'neutral' {
  const s = status.toLowerCase();
  if (s === 'completed' || s === 'done' || s === 'success') return 'success';
  if (s === 'in_progress' || s === 'pending' || s === 'processing') return 'warning';
  return 'neutral';
}

/** Reusable rows-per-page + pager block (mirrors the DNS list footer). */
function PagerBar({
  id,
  page,
  pageSize,
  pageCount,
  isFetching,
  onPage,
  onPageSize,
}: {
  id: string;
  page: number;
  pageSize: number;
  pageCount: number;
  isFetching: boolean;
  onPage: (p: number) => void;
  onPageSize: (s: number) => void;
}) {
  if (pageCount <= 1 && PAGE_SIZE_OPTIONS.length <= 1) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--vn-space-4)',
        marginTop: 'var(--vn-space-4)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <label htmlFor={id} style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          Rows per page
        </label>
        <Select value={String(pageSize)} onValueChange={(v) => onPageSize(Number(v))}>
          <SelectTrigger id={id} size="sm" style={{ width: 72 }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((s) => (
              <SelectItem key={s} value={String(s)}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {pageCount > 1 && (
        <Pagination
          page={page}
          pageCount={pageCount}
          onPageChange={onPage}
          summary={isFetching ? 'Loading…' : `Page ${page} of ${pageCount}`}
        />
      )}
    </div>
  );
}

/** Tiered-cache toggle — set-only (the BE has no getter for the current state). */
function TieredCacheCard({ serviceId, domain }: { serviceId: string; domain: string }) {
  const { show } = useToast();
  const [on, setOn] = useState(false);
  // Reset the (unknown) local state when the domain changes.
  useEffect(() => setOn(false), [domain]);

  const set = useSetTieredCache(serviceId, {
    onSuccess: (_d, vars) => {
      show({ tone: 'success', title: vars.enable ? 'Tiered cache enabled' : 'Tiered cache disabled' });
      trackFeatureAction('waf', 'cache', 'toggle');
    },
    onError: (e, vars) => {
      setOn(!vars.enable); // revert optimistic flip
      show({ tone: 'danger', title: wafExtractError(e, 'Update failed') });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tiered cache</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-4)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ fontWeight: 500 }}>Multi-layer caching</span>
            <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
              Adds a mid-tier cache between the edge and your origin. The current state can't be read back, so this is a set-only control.
            </span>
          </div>
          <Switch
            checked={on}
            aria-label="Tiered cache"
            disabled={set.isPending}
            onCheckedChange={(v) => {
              setOn(v);
              set.mutate({ domain, enable: v });
            }}
          />
        </div>
      </CardBody>
    </Card>
  );
}

/** Row actions: edit + delete a cache rule. */
function CacheRuleActionsCell({
  serviceId,
  domain,
  rule,
  scopes,
}: {
  serviceId: string;
  domain: string;
  rule: WafCacheRule;
  scopes: WafCacheOption[];
}) {
  const { show } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteWafCacheRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Cache rule deleted' }); trackFeatureAction('waf', 'cache_rule', 'delete'); setConfirmDelete(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        <Tooltip content="Edit" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Edit cache rule" onClick={() => setEditOpen(true)}>
            <Pencil size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Delete" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Delete cache rule" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={14} />
          </IconButton>
        </Tooltip>
      </TooltipProvider>

      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete cache rule?"
          description={`Delete the ${rule.scope} rule for "${rule.paths[0] ?? ''}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ domain, id: rule.id })}
        />
      )}

      {editOpen && (
        <CacheRuleFormDialog
          serviceId={serviceId}
          domain={domain}
          scopes={scopes}
          rule={rule}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </div>
  );
}

/** Cache rules — cached/no-cache tabs + scope filter + paginated table + full CRUD. */
function CacheRulesCard({ serviceId, domain }: { serviceId: string; domain: string }) {
  const optionsQ = useWafCacheOptions(serviceId);
  const scopes = optionsQ.data?.scopes ?? [];

  // Split caching behaviour into tabs: cached (TTL > 0) vs no-cache (TTL = 0).
  const [tab, setTab] = useState<'cached' | 'bypass'>('cached');
  const [scope, setScope] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [createOpen, setCreateOpen] = useState(false);

  const isBypass = tab === 'bypass';

  // Reset paging on tab/domain change; reset tab + scope when the domain changes.
  useEffect(() => { setPage(1); }, [domain, tab]);
  useEffect(() => { setTab('cached'); setScope('all'); }, [domain]);

  const { data, isLoading, isError, isFetching, refetch } = useWafCacheRules(serviceId, domain, {
    bucket: tab,
    scope: scope === 'all' ? undefined : scope,
    page,
    page_size: pageSize,
  });

  const rows = data?.items ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, data?.pagination?.total_pages ?? 1);
  const hasFilter = scope !== 'all';

  const columns = useMemo<ColumnDef<WafCacheRule, unknown>[]>(
    () => {
      const cols: ColumnDef<WafCacheRule, unknown>[] = [
        {
          id: 'scope',
          header: 'Scope',
          enableSorting: false,
          cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.scope}</Badge>,
        },
        {
          id: 'paths',
          header: 'Paths',
          enableSorting: false,
          cell: ({ row }) => pathsCell(row.original.paths),
        },
      ];
      // TTL / honor-origin only matter for cached rules; no-cache rules are all bypass.
      if (!isBypass) {
        cols.push(
          {
            id: 'ttl',
            header: 'TTL',
            enableSorting: false,
            cell: ({ row }) => <span style={mono}>{row.original.ttl}s</span>,
          },
          {
            id: 'honor-origin',
            header: 'Honor origin',
            enableSorting: false,
            cell: ({ row }) => (row.original.honor_origin ? 'Yes' : muted('No')),
          },
        );
      }
      cols.push({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <CacheRuleActionsCell serviceId={serviceId} domain={domain} rule={row.original} scopes={scopes} />
        ),
      });
      return cols;
    },
    [isBypass, serviceId, domain, scopes],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cache rules{total ? ` (${total})` : ''}</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.tabsRow}>
          <Tabs value={tab} onValueChange={(v) => setTab(v as 'cached' | 'bypass')}>
            <TabsList>
              <TabsTrigger value="cached">Cache rules</TabsTrigger>
              <TabsTrigger value="bypass">No cache rule</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            <Select value={scope} onValueChange={(v) => { setScope(v); setPage(1); }}>
              <SelectTrigger aria-label="Filter by scope" className={styles.filterSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scopes</SelectItem>
                {scopes.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={styles.actions}>
            <Button variant="secondary" startIcon={<RotateCcw size={15} />} onClick={() => refetch()} aria-label="Refresh cache rules">
              Refresh
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Add rule</Button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
          </div>
        ) : isError ? (
          <Alert tone="danger" title="Couldn't load cache rules" actions={<button onClick={() => refetch()}>Retry</button>}>
            The WAF service couldn't return this domain's cache rules. Try again.
          </Alert>
        ) : total === 0 && !hasFilter ? (
          <EmptyState
            icon={<Gauge size={20} />}
            title={isBypass ? 'No no-cache rules yet' : 'No cache rules yet'}
            description={
              isBypass
                ? 'Add a no-cache rule to bypass caching for matching requests.'
                : 'Add a rule to control how this domain\'s content is cached.'
            }
            actions={<Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Add rule</Button>}
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(r) => String(r.id)}
              emptyState="No cache rules match this filter."
              footer={`Showing ${rows.length ? (page - 1) * pageSize + 1 : 0}–${(page - 1) * pageSize + rows.length} of ${total}`}
            />
            <PagerBar
              id="waf-cache-rules-page-size"
              page={page}
              pageSize={pageSize}
              pageCount={pageCount}
              isFetching={isFetching}
              onPage={setPage}
              onPageSize={(s) => { setPageSize(s); setPage(1); }}
            />
          </>
        )}
      </CardBody>

      {createOpen && (
        <CacheRuleFormDialog
          serviceId={serviceId}
          domain={domain}
          scopes={scopes}
          defaultCached={!isBypass}
          open={createOpen}
          onOpenChange={setCreateOpen}
        />
      )}
    </Card>
  );
}

/** Cache purges — submit a purge + paginated history. */
function CachePurgesCard({ serviceId, domain }: { serviceId: string; domain: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [purgeOpen, setPurgeOpen] = useState(false);

  useEffect(() => setPage(1), [domain]);

  const { data, isLoading, isError, isFetching, refetch } = useWafCachePurges(serviceId, domain, {
    page,
    page_size: pageSize,
  });

  const rows = data?.items ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, data?.pagination?.total_pages ?? 1);

  const columns = useMemo<ColumnDef<WafCachePurge, unknown>[]>(
    () => [
      {
        id: 'paths',
        header: 'Paths',
        enableSorting: false,
        cell: ({ row }) => pathsCell(row.original.paths),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone={purgeTone(row.original.status)} appearance="subtle" size="sm">
            {row.original.status || '—'}
          </Badge>
        ),
      },
      {
        id: 'created',
        header: 'Submitted',
        enableSorting: false,
        cell: ({ row }) => muted(fmtEpoch(row.original.created_at)),
      },
    ],
    [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cache purges{total ? ` (${total})` : ''}</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.toolbar}>
          <div className={styles.filters} />
          <div className={styles.actions}>
            <Button variant="secondary" startIcon={<RotateCcw size={15} />} onClick={() => refetch()} aria-label="Refresh purge history">
              Refresh
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={() => setPurgeOpen(true)}>Purge cache</Button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
          </div>
        ) : isError ? (
          <Alert tone="danger" title="Couldn't load purge history" actions={<button onClick={() => refetch()}>Retry</button>}>
            The WAF service couldn't return this domain's purge history. Try again.
          </Alert>
        ) : total === 0 ? (
          <EmptyState
            icon={<Gauge size={20} />}
            title="No purges yet"
            description="Submit a purge to invalidate cached content for specific paths."
            actions={<Button startIcon={<Plus size={15} />} onClick={() => setPurgeOpen(true)}>Purge cache</Button>}
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(r) => String(r.id)}
              emptyState="No purges yet."
              footer={`Showing ${rows.length ? (page - 1) * pageSize + 1 : 0}–${(page - 1) * pageSize + rows.length} of ${total}`}
            />
            <PagerBar
              id="waf-cache-purges-page-size"
              page={page}
              pageSize={pageSize}
              pageCount={pageCount}
              isFetching={isFetching}
              onPage={setPage}
              onPageSize={(s) => { setPageSize(s); setPage(1); }}
            />
          </>
        )}
      </CardBody>

      {purgeOpen && (
        <CachePurgeDialog serviceId={serviceId} domain={domain} open={purgeOpen} onOpenChange={setPurgeOpen} />
      )}
    </Card>
  );
}

export function WafCacheControl({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  // A zone is selected but has no proxied subdomain → nothing to manage.
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Gauge size={22} />}
        title="Cache Control"
        description="Manage CDN cache rules, tiered caching and purges for the selected subdomain."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Layers size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to manage cache for. Proxy a record to control its cache."
        />
      ) : !domain ? (
        <EmptyState
          icon={<Layers size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to manage its cache."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <TieredCacheCard serviceId={serviceId} domain={domain} />
          <CacheRulesCard serviceId={serviceId} domain={domain} />
          <CachePurgesCard serviceId={serviceId} domain={domain} />
        </div>
      )}
    </Container>
  );
}

export default WafCacheControl;
